/**
 * DataTableToolbar Component
 * 
 * Flex container for tabs, filters, and primary action button.
 */

import { type ReactNode, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';

export interface DataTableToolbarProps {
  readonly children: ReactNode;
  readonly className?: string;
}

export function DataTableToolbar({ children, className }: DataTableToolbarProps) {
  return (
    <div 
      role="toolbar" 
      aria-label="Table controls"
      className={cn('flex flex-col items-start gap-3 md:flex-row md:items-start', className)}
    >
      {children}
    </div>
  );
}

export interface DataTableToolbarFiltersProps {
  readonly children: ReactNode;
  /** Slot for action button(s) - rendered after filters */
  readonly actions?: ReactNode;
  /** Active filter badges to render below filters (right-aligned) */
  readonly activeFilters?: ReactNode;
  readonly className?: string;
}

export function DataTableToolbarFilters({
  children,
  actions,
  activeFilters,
  className
}: DataTableToolbarFiltersProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    const container = containerRef.current;
    if (!container) return;

    const buttons = Array.from(
      container.querySelectorAll<HTMLButtonElement>('button[aria-expanded]')
    );
    
    if (buttons.length === 0) return;

    const currentIndex = buttons.findIndex((btn) => btn === document.activeElement);
    if (currentIndex === -1) return;

    e.preventDefault();

    let nextIndex: number;
    if (e.key === 'ArrowLeft') {
      nextIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === buttons.length - 1 ? 0 : currentIndex + 1;
    }

    buttons[nextIndex]?.focus();
  }, []);

  return (
    <div className={cn('flex flex-col items-start gap-6 md:items-end md:ml-auto', className)}>
      <div 
        ref={containerRef}
        role="group" 
        aria-label="Filters"
        className="flex items-center gap-2 flex-wrap"
        onKeyDown={handleKeyDown}
      >
        {children}
        {actions}
      </div>

      {activeFilters && (
        <div className="flex justify-end">
          {activeFilters}
        </div>
      )}
    </div>
  );
}
